import React, { FC } from 'react';
import { useIntl } from "react-intl";
import { HeaderModel } from '../../../models/CommonModel';
import SortableColumn from '../../../components/Table/SortableColumn';
import { formatPrice } from '../../../selectors';
import { SaleRowCompareModel, SaleRowModel } from '../../../models/account-books/SalesAccount';
import moment from 'moment';
import { MstTaxGroupModel } from '../../../models/MstTaxGroupModel';
import { DH_LINE_STATUS_LABEL } from '../../../constants/common';

type Props = {
  data: Array<SaleRowModel>,
  compareRows?: Array<SaleRowCompareModel>,
  header: HeaderModel[]
  hideCols: string[],
  openColConfig: () => void,
  handleSort: (sortBy: string, direction: 'asc' | 'desc' | 'none') => void
  showCompareModal: (originData: SaleRowModel, compareData: SaleRowModel) => void
};

const SalesAccountTable: FC<Props> = ({ data, compareRows, header, hideCols, openColConfig, handleSort, showCompareModal}) => {
  const intl = useIntl()
  const renderTaxGroup = (taxGroup: MstTaxGroupModel) => {
    if (!taxGroup) {
      return '';
    }
    return (
      <>
        {taxGroup.name} (GTGT: {Number(taxGroup.vat_rate)}%; TNCN: {Number(taxGroup.pit_rate)}%)
      </>
    )
  }

  const renderStatus = (status: string) => {
    const key = status as keyof typeof DH_LINE_STATUS_LABEL;

    const color = DH_LINE_STATUS_LABEL[key]?.color ?? 'default';
    const label = DH_LINE_STATUS_LABEL[key]?.label ?? '';
    return (
      <span className={'d-flex align-items-center badge bg-light-' + color + ' text-' + color}>
        {label}
      </span>
    )
  }

  const normalizeDate = (date: string) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString().slice(0, 10);
  };

  const buildKey = (row: any) => {
    const round = (n: number) => Number((n ?? 0).toFixed(2));
    return [
      (row.invoice_code ?? '')+(row.invoice_no ?? ''),
      normalizeDate(row.invoice_date),
      row.product_name ?? '',
      round(row.unit_price),
      round(row.quantity),
      round(row.vat),
      round(row.line_amount),
    ]
      .map(v => String(v).trim())
      .join('|');
  };

  const isMatch = (saleLine: SaleRowModel) => {
    if (!compareRows) {
      return false;
    }
    const compareSet = new Set(compareRows.map(buildKey));

    return compareSet.has(buildKey(saleLine))
  }

  return (
    <div className='table-responsive'>
      <table className="table table-hover table-bordered table-striped border gy-7 gs-7">
        <thead>
          <tr className="fw-bold fs-6 text-gray-800 border-bottom-2 border-gray-200">
            <th>
              <i
                className="fa fa-cogs"
                style={{ cursor: 'pointer' }}
                onClick={openColConfig}
              ></i>
            </th>
            {header.map((th, index) => {
              if (!hideCols.includes(th.field)) {
                if (th.sortable) {
                  return (
                    <SortableColumn
                      key={'th-' + index}
                      sortBy={th?.field}
                      onSortChange={handleSort}
                      style={th?.style ?? {}}
                    >
                      {th.label}
                    </SortableColumn>
                  )
                }
                return (
                  <th style={th?.style ?? {}}>{th.label}</th>
                )
              }
            })}
          </tr>
        </thead>
        <tbody>
          {data?.map((item: SaleRowModel, index: number) => {
            let qty = item.quantity - (item.return_qty ?? 0)
            let lineAmount = item.line_amount - (item.unit_price * (item.return_qty ?? 0));
            let itemStatus = item.status as keyof typeof DH_LINE_STATUS_LABEL;
            const isMissing = item.id === 0;
            if (compareRows?.length && compareRows?.length > 0 && itemStatus !== 'verified') {
              if (isMissing) {
                itemStatus = 'missing'
              } else {
                itemStatus = isMatch(item) ? 'matched' : 'mismatch';
              }
            }
            
            return (
              <tr key={`agent_${index}`} className="cursor-pointer">
                <td></td>
                {!hideCols.includes('created_at') && (
                  <td>
                    {item.created_at ? moment(item.created_at).format('DD-MM-YYYY') : ''}
                  </td>
                )}
                {!hideCols.includes('invoice_no') && (
                  <td>{item?.invoice_no ?? ''}</td>
                )}
                {!hideCols.includes('invoice_date') && (
                  <td>{item?.invoice_no ? moment(item.invoice_date).format('DD-MM-YYYY') : ''}</td>
                )}
                {!hideCols.includes('product_name') && (
                  <td >{item.product_name}</td>
                )}
                {!hideCols.includes('quantity') && (
                  <td style={{ textAlign: "right" }}>
                    {formatPrice(qty, false)}
                  </td>
                )}
                {!hideCols.includes('line_amount') && (
                  <td style={{ textAlign: "right" }}>{formatPrice(lineAmount)}</td>
                )}
                {!hideCols.includes('tax_group') && (
                  <td >{item.id !== 0 ? renderTaxGroup(item.mst_tax_group) : ''}</td>
                )}
                {!hideCols.includes('status') && (
                  <td>
                    <div className='d-flex'>
                      {renderStatus(itemStatus)}
                      <button
                        onClick={() => showCompareModal(item, item)}
                        className='btn btn-icon btn-bg-light btn-sm ms-1'>
                        <i className='bi bi-eye fs-4'></i>
                      </button>
                    </div>
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  );
}

export default SalesAccountTable;